import { z } from 'zod';

import { ZodLanguageCode } from '@Common/types/language';
import { ZodPappersLegalInfo } from '@Common/types/legal_representatives';
import { ZodGetInvestRangesResponse } from '@Common/types/questions/invest_ranges';
import { ZodValidUBODetailsBase } from '@Common/types/ubo/details';
import { ZodCountryCode } from '@Common/types/zod/zod-country-code';
import { ZodDateString } from '@Common/types/zod/zod-date-string';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { createZodMaxBase64SizeValidator } from '@Common/types/zod/zod-max-base64-size';
import { createZodParamsValidator } from '@Common/types/zod/zod-params';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

export const ZodGetQuestionsCompanyRangesCountryCodeParams = createZodParamsValidator(
  z.object({
    country_code: ZodTrimmedString,
  }),
);

export const ZodGetQuestionsCompanyRangesCountryCodeQuery = createZodParamsValidator(
  z.object({
    language: ZodLanguageCode,
  }),
);

export const getQuestionsCompanyInvestRangesCountryCodeEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodGetQuestionsCompanyRangesCountryCodeParams,
  queryParams: ZodGetQuestionsCompanyRangesCountryCodeQuery,
  responseBody: ZodGetInvestRangesResponse,
});

export type GetQuestionsCompanyInvestRangesCountryCodeEndpoint = GetEndpointType<
  typeof getQuestionsCompanyInvestRangesCountryCodeEndpoint
>;
export const ZodGetQuestionsCompanySiretParams = createZodParamsValidator(
  z.object({
    siret: ZodTrimmedString,
  }),
);

export const ZodValidUBODetails = z.object({
  ...ZodValidUBODetailsBase.shape,
  birth_date: ZodDateString,
});

export type ValidUBODetails = z.infer<typeof ZodValidUBODetails>;

export const ZodRawUBODetails = z.object({
  first_name: ZodTrimmedString.nullable(),
  last_name: ZodTrimmedString.nullable(),
  birth_date: ZodTrimmedString.nullable(),
  birth_city: ZodTrimmedString.nullable(),
  citizenship: ZodTrimmedString.nullable(),
  country_of_tax_residence: ZodTrimmedString.nullable(),
  legal_info: ZodPappersLegalInfo.optional(),
});

export type RawUBODetails = z.infer<typeof ZodRawUBODetails>;

export const ZodUbos = z.object({
  valid: z.array(ZodValidUBODetails),
  invalid: z.array(ZodRawUBODetails).optional(),
});

export const ZodQuestionsCompanySiretUbosResponseBody = z.object({
  ubos: ZodUbos,
});

export const getQuestionsCompanySiretUbosEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodGetQuestionsCompanySiretParams,
  responseBody: ZodQuestionsCompanySiretUbosResponseBody,
});

export type GetQuestionsCompanySiretUbosEndpoint = GetEndpointType<
  typeof getQuestionsCompanySiretUbosEndpoint
>;

export const getQuestionsCompanyRevenueRangesCountryCodeEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodGetQuestionsCompanyRangesCountryCodeParams,
  queryParams: ZodGetQuestionsCompanyRangesCountryCodeQuery,
  responseBody: ZodGetInvestRangesResponse,
});

export type GetQuestionsCompanyRevenueRangesCountryCodeEndpoint = GetEndpointType<
  typeof getQuestionsCompanyRevenueRangesCountryCodeEndpoint
>;

export const ZodCompanyDirector = z.object({
  first_name: ZodTrimmedString,
  last_name: ZodTrimmedString,
  position: ZodTrimmedString,
  birth_date: ZodDateString.nullable(),
  birth_city: ZodTrimmedString.nullable(),
  citizenship: ZodCountryCode.nullable(),
  sex: z.enum(['Male', 'Female']).nullable(),
  legal_info: ZodPappersLegalInfo.optional(),
});
export type CompanyDirector = z.infer<typeof ZodCompanyDirector>;

export const ZodQuestionsCompanySiretDirectorsResponseBody = z.object({
  directors: z.array(ZodCompanyDirector),
});

export const getQuestionsCompanySiretDirectorsEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodGetQuestionsCompanySiretParams,
  responseBody: ZodQuestionsCompanySiretDirectorsResponseBody,
});

export type GetQuestionsCompanySiretDirectorsEndpoint = GetEndpointType<
  typeof getQuestionsCompanySiretDirectorsEndpoint
>;

export const ZodPostQuestionsCompanySiretDocumentsBody = z.object({
  document_files: z.array(createZodMaxBase64SizeValidator('10mb')),
  document_name: ZodTrimmedString,
});
export type PostQuestionsCompanySiretDocumentsBody = z.infer<
  typeof ZodPostQuestionsCompanySiretDocumentsBody
>;

export const postQuestionsCompanySiretDocumentsEndpoint = endpoint({
  method: 'POST',
  pathParams: ZodGetQuestionsCompanySiretParams,
  body: ZodPostQuestionsCompanySiretDocumentsBody,
  responseBody: z.object({
    files: z.array(
      z.object({
        name: ZodTrimmedString,
        path: ZodTrimmedString,
      }),
    ),
  }),
});

export type PostQuestionsCompanySiretDocumentsEndpoint = GetEndpointType<
  typeof postQuestionsCompanySiretDocumentsEndpoint
>;
